import { useEffect, useState } from 'react';
import { T } from '../components/text/TextEdit';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Diamond, Grid3x3, Package, Plus, Ruler, Smartphone } from 'lucide-react';
import { loadHomeSummary } from '../lib/homeSummary';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/pipeline/ui';
import { SourceBadge } from '../components/pipeline/charts';
import NewIdeaDialog from '../components/NewIdeaDialog';

// The three artifact dashboards — Ideas, Market analyses, Build packs.
//
// Card anatomy and, more importantly, WHICH CARDS HAVE A BUTTON, are taken from the mock rather
// than invented. The first version of this file put an action button on every card, including the
// Ideas cards where the mock has none, and then needed a label for a control that should not have
// existed — which is how every idea card ended up saying "back". Mock rule R1: never draw something
// that was not asked for.
//
// The rule the mock encodes: a card whose artifact is FINISHED is itself the link, with no button.
// A card with work still to do is not clickable and carries the button that starts that work.

const CHIP = 'rounded px-2 py-0.5 text-[11px]';
const CHIP_MUTED = `${CHIP} bg-muted text-muted-foreground`;
const CHIP_OK = `${CHIP} border border-success-border bg-success-soft text-success`;

const ICONS = { ideas: Diamond, markets: Grid3x3, packs: Package };
// Which artifact each dashboard opens. Without this every card landed on the idea page.
const STAGE_ROUTE = { ideas: '', markets: '/market', packs: '/build' };
const TITLE_KEY = { ideas: 'stepper.idea', markets: 'stepper.market', packs: 'stepper.build' };

// Returns { chip, chipOk, descKey, actionKey, open } for one card in one dashboard.
// `open` true means the whole card is the link and there is no button.
function stateOf(kind, c, t) {
  if (kind === 'ideas') {
    return {
      chip: typeof c.score === 'number' ? `${c.score} / 100` : null,
      chipOk: true,
      actionKey: null,
      open: true,
    };
  }
  if (kind === 'markets') {
    if (c.market === 'done') {
      return {
        chip: t('market.done'),
        chipOk: true,
        actionKey: null,
        open: true,
        footScore: typeof c.market_score === 'number' ? `${c.market_score} / 100` : null,
      };
    }
    if (c.market === 'partial') {
      return { chip: t('market.analyzing'), descKey: 'market.resumeHint', actionKey: 'market.resume', open: false };
    }
    return { chip: t('market.empty'), actionKey: 'market.analyze', open: false };
  }
  // Packs cards carry the two DOORS (Ali picked option 2, 2026-08-12): every idea can leave as
  // a Blueprint (prompts) or a Prototype (working mock app), one click from this list. The card
  // is never itself the link — the doors are the only ways in, so neither path is a default.
  if (c.pack === 'ready') return { chip: t('build.ready'), chipOk: true, actionKey: null, open: false, doors: true };
  return { chip: t('build.empty'), actionKey: null, open: false, doors: true };
}

function ArtifactCard({ card, kind, onOpen }) {
  const { t } = useTranslation();
  const Icon = ICONS[kind];
  const st = stateOf(kind, card, t);

  // Three of eight ideas in real data have name === one_liner, so printing both renders the same
  // sentence twice. The duplication is in the generated data; not repeating it is the UI's job.
  const desc = st.descKey
    ? t(st.descKey)
    : (card.one_liner && card.one_liner !== card.name ? card.one_liner : '');

  const body = (
    <>
      <div className="flex items-start justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-border">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </span>
        {st.chip && <span className={st.chipOk ? CHIP_OK : CHIP_MUTED}>{st.chip}</span>}
      </div>

      <h3 className="mt-3 font-heading font-semibold" title={card.name || ''}>{card.name || '—'}</h3>
      <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-xs text-muted-foreground">{desc}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {st.footScore && <span className={CHIP_OK}>{st.footScore}</span>}
        {/* Read from the artifact, per card. No recorded label means no badge — never a defaulted
            one, which would assert something nobody checked. */}
        {card.score_label ? <SourceBadge source={card.score_label} /> : null}
      </div>

      {st.actionKey && (
        <Button
          className="mt-4 w-full"
          onClick={() => onOpen(card.project_id, true)}
          data-testid={`action-${kind}-${card.project_id}`}
        >
          {t(st.actionKey)}
        </Button>
      )}

      {st.doors && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpen(card.project_id, false, '/build')}
            data-testid={`door-blueprint-${card.project_id}`}
          >
            <Ruler className="me-1.5 h-3.5 w-3.5" /> {t('fork.blueprint')}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpen(card.project_id, false, '/prototype')}
            data-testid={`door-prototype-${card.project_id}`}
          >
            <Smartphone className="me-1.5 h-3.5 w-3.5" /> {t('fork.prototype')}
          </Button>
        </div>
      )}
    </>
  );

  const cls = 'flex flex-col rounded-lg border border-border bg-card p-5';
  const testId = `card-${kind}-${card.project_id}`;

  // A finished card IS the link. An unfinished one is not clickable at all — its button is the only
  // way in, so the work it starts is always a deliberate click.
  return st.open ? (
    <button type="button" className={`${cls} text-start hover:border-primary`} data-testid={testId}
      onClick={() => onOpen(card.project_id)}>
      {body}
    </button>
  ) : (
    <div className={cls} data-testid={testId}>{body}</div>
  );
}

// Newest first, by date and time. created_at is what the get_home_summary handler selects; the
// other names and the ISO scan are fallbacks so a server rename degrades to "still sorted" rather
// than "silently unsorted".
const ISO = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
function dateKeyOf(list) {
  const sample = list.find((c) => c && typeof c === 'object');
  if (!sample) return null;
  for (const k of ['created_at', 'createdAt', 'updated_at', 'updatedAt']) {
    if (typeof sample[k] === 'string' && ISO.test(sample[k])) return k;
  }
  return Object.keys(sample).find((k) => typeof sample[k] === 'string' && ISO.test(sample[k])) || null;
}
function newestFirst(list) {
  const arr = Array.isArray(list) ? [...list] : [];
  const key = dateKeyOf(arr);
  if (!key) {
    // Loud on purpose. If this fires, the cards carry no timestamp and the fix is server-side.
    console.warn('[dashboard] no timestamp on the cards - left in server order', arr[0] && Object.keys(arr[0]));
    return arr;
  }
  console.info('[dashboard] sorting newest first on', key);
  return arr
    .map((c, i) => [c, i, Date.parse(c?.[key])])
    .sort((a, b) => {
      const ta = Number.isNaN(a[2]) ? null : a[2];
      const tb = Number.isNaN(b[2]) ? null : b[2];
      if (ta === null && tb === null) return a[1] - b[1];
      if (ta === null) return 1;
      if (tb === null) return -1;
      return tb - ta || a[1] - b[1];
    })
    .map((x) => x[0]);
}
export default function ArtifactDashboard({ kind }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cards, setCards] = useState(null);
  const [failed, setFailed] = useState(false);
  // Creating an idea used to live on the projects page. That page is gone, so the action lives
  // where the ideas are. The mock only offers creation from the EMPTY home, which leaves someone
  // with eight ideas nowhere to click - a gap in the mock rather than a decision (rule R5).
  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    let live = true;
    loadHomeSummary()
      .then((d) => { if (live) setCards(newestFirst(d.cards ?? [])); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  const title = t(TITLE_KEY[kind]);

  if (failed) {
    return (
      <>
        <h1 className="font-heading text-3xl font-bold">{title}</h1>
        <p className="mt-1 text-muted-foreground"><T k="common.loadFailed" /></p>
      </>
    );
  }

  if (!cards) {
    return (
      <>
        <Skeleton className="h-9 w-56" />
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">{title}</h1>
          <p className="mt-1 text-muted-foreground"><T k="dashboard.subtitle" /></p>
        </div>
        {kind === 'ideas' && (
          <Button onClick={() => setNewOpen(true)} data-testid="new-idea-button">
            <Plus className="me-2 h-4 w-4" /> {t('dashboard.newProject')}
          </Button>
        )}
      </div>
      {kind === 'ideas' && (
        <NewIdeaDialog open={newOpen} onOpenChange={setNewOpen} caps={['idea_input', 'idea_suggest']} />
      )}
      {cards.length === 0 ? (
        <div className="mt-6 rounded-lg border-2 border-dashed border-border p-16 text-center"
          data-testid={`empty-${kind}`}>
          <p className="font-medium"><T k="dashboard.emptyTitle" /></p>
          <p className="mt-1 text-sm text-muted-foreground"><T k="dashboard.emptySubtitle" /></p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid={`grid-${kind}`}>
          {cards.map((c) => (
            <ArtifactCard key={c.project_id} card={c} kind={kind}
              onOpen={(id, act, stageOverride) =>
                // act means the card's ACTION button was hit, not the card itself. For markets
                // that click already says "analyze" (or "resume"), so it travels as ?start=<nonce>
                // and MarketStage starts without demanding the same click a second time. The nonce
                // is unique per click: MarketStage consumes each value once at module level, so a
                // dev-mode double mount cannot fire two runs off one click (measured 2026-08-03:
                // ?start=1 started two concurrent analyses, two versions appended 5s apart).
                // stageOverride: the packs cards carry two DOOR buttons (blueprint/prototype),
                // each naming its own stage route.
                navigate(`/project/${id}${stageOverride ?? STAGE_ROUTE[kind]}${act && kind === 'markets' ? `?start=${Date.now()}` : ''}`)} />
          ))}
        </div>
      )}
    </>
  );
}
